import { AnchorHTMLAttributes, Children, cloneElement, forwardRef, type PropsWithChildren, type ReactElement } from 'react';

import * as s from './ProgressIndicator.css';
import { ChevronRightIcon } from '../../icons';
import { useI18nTranslations } from '../../providers/i18n';
import { getClassNames } from '../../util/css';
import { Stack } from '../Stack/Stack';
import { VisuallyHidden } from '../VisuallyHidden/VisuallyHidden';

type ProgressIndicatorItemProps = Pick<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'onClick' | 'onMouseEnter'> & {
  /** Link text. */
  children: string;
  /** Indicates whether the step is currently active. */
  isActive?: boolean;
  /** Indicates whether the step is not yet accessible. */
  isDisabled?: boolean;
  /** Hide inactive labels. This prop can only be inserted programatically */
  hideInactiveLabel?: never;
  /** Allow overwrite the anchore tag, when the href property is not required and the onClick is used to
   * execute the navigation.
   */
  as?: 'a' | 'span';
};

const ProgressIndicatorItem = forwardRef<HTMLAnchorElement, ProgressIndicatorItemProps>((props, ref) => {
  const { children, isActive, isDisabled, hideInactiveLabel, as: elementAs, ...linkProps } = props;
  const { current, completed } = useI18nTranslations();

  const itemClassName = getClassNames(
    s.itemStyle,
    isDisabled && s.itemDisabledStyle,
    isActive && s.itemActiveStyle,
  );

  const labelClassName = s.labelRecipe({
    isActive: !!isActive,
    isDisabled: !!isDisabled,
    hideInactiveLabel: !!hideInactiveLabel,
  });

  const textClassName = s.textRecipe({
    isActive: !!isActive,
    hideInactiveLabel: !!hideInactiveLabel,
  });

  const linkContent = (
    <>
      <div className={textClassName} data-hidden={hideInactiveLabel}>
        {children}
      </div>
      <VisuallyHidden>{`(${completed})`}</VisuallyHidden>
    </>
  );

  const renderLabel = () => {
    if (isDisabled || isActive) {
      return (
        <span className={labelClassName} aria-disabled={isDisabled}>
          <div className={textClassName} data-hidden={hideInactiveLabel}>
            {/* To prevent React's NotFoundError during node insertion,
             we maintain a consistent DOM structure,
             by always rendering the same components and controlling their visibility with styles.
             See: https://github.com/facebook/react/issues/11538 */}
            <span style={{ visibility: isActive ? 'visible' : 'hidden' }}>
              {<VisuallyHidden>{`${current}: `}</VisuallyHidden>}
            </span>
            {children}
          </div>
        </span>
      );
    }

    if (elementAs === 'span') {
      return (
        <span className={labelClassName} {...linkProps}>
          {linkContent}
        </span>
      );
    }

    return (
      <a className={labelClassName} {...linkProps} ref={ref}>
        {linkContent}
      </a>
    );
  };

  return (
    <li className={itemClassName}>
      <Stack direction="row" gap={{ initial: '3', md: '5' }} alignX="justify" alignY="center">
        {renderLabel()}
        <ChevronRightIcon size="small" color="currentColor" />
      </Stack>
    </li>
  );
});

type ProgressIndicatorProps = {
  className?: never;
  /** Minimum amount of children that will be used to determine if the labels should be hidden */
  minChildrenToHideLabels?: number;
};

const ProgressIndicatorComponent = forwardRef<HTMLDivElement, PropsWithChildren<ProgressIndicatorProps>>(
  ({ className, children, minChildrenToHideLabels = 6 }, ref) => {
    const { steps } = useI18nTranslations();
    const childrenCount = Children.count(children);

    const hideInactiveLabels = childrenCount >= minChildrenToHideLabels;
    let augmentedChildren;

    if (hideInactiveLabels) {
      augmentedChildren = Children.map(children, child => {
        return cloneElement(child as ReactElement<{ hideInactiveLabel: boolean }>, {
          hideInactiveLabel: true,
        });
      });
    }

    return (
      <nav aria-label={steps} className={s.navStyle}>
        <Stack
          as={'ol'}
          direction={'row'}
          gap={{ initial: '3', md: '5' }}
          alignX="center"
          className={className}
          ref={ref}>
          {augmentedChildren ?? children}
        </Stack>
      </nav>
    );
  },
);

ProgressIndicatorItem.displayName = 'ProgressIndicator.Item';
ProgressIndicatorComponent.displayName = 'ProgressIndicator';

// Compound components with forwardRef cause issues: https://github.com/DefinitelyTyped/DefinitelyTyped/issues/34757#issuecomment-488848720
export const ProgressIndicator = {
  ...ProgressIndicatorComponent,
  Item: ProgressIndicatorItem,
} as typeof ProgressIndicatorComponent & { Item: typeof ProgressIndicatorItem };
